import { useState, useMemo } from "react";
import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { Kpi } from "@/components/admin/kpi";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import { AdminIcon } from "@/components/admin/icon";
import { useToast } from "@/components/ui/feedback";
import { usePermissions } from "@/hooks/use-permissions";
import {
	useReferralConfig,
	useReferralLeaderboard,
	useReferrals,
} from "@/lib/customers/customers-hooks";
import { customersApi } from "@/lib/customers/customers-api";
import type { TagClass } from "@/lib/status";
import type { Referral } from "@/lib/customers/types";

const num = (v: unknown): string =>
	v === undefined || v === null ? "—" : String(v);

const formatFcfa = (v: unknown): string => {
	if (v === undefined || v === null || v === "" || isNaN(Number(v))) return "—";
	return `${Number(v).toLocaleString("fr-FR")} FCFA`;
};

const STATUS_VARIANT: Record<string, TagClass> = {
	rewarded: "success",
	qualified: "info",
	pending: "neutral",
	flagged: "terra",
	expired: "warning",
};

const STATUS_LABEL: Record<string, string> = {
	rewarded: "Récompensé",
	qualified: "Qualifié",
	pending: "En attente",
	flagged: "Suspect / Fraude",
	expired: "Expiré",
};

export function AdminCustomerReferralsPage() {
	const toast = useToast();
	const perms = usePermissions();

	const [statusFilter, setStatusFilter] = useState<string>("");
	const [searchQuery, setSearchQuery] = useState<string>("");

	const { data: referrals, isLoading: isRefLoading, isError: isRefError } = useReferrals(statusFilter || undefined);
	const { data: leaderboard, isLoading: isLbLoading } = useReferralLeaderboard();
	const { data: config, isLoading: isCfgLoading, refetch: refetchConfig } = useReferralConfig();

	const canEditConfig = perms.has("LOYALTY.CONFIG") || perms.isSuperAdmin;

	const [showConfigModal, setShowConfigModal] = useState(false);
	const [configForm, setConfigForm] = useState({
		sponsorRewardAmount: "",
		qualifyingOrderMin: "",
		refereeExpiryDays: "",
		rewardDelayDays: "",
		fraudBlockThreshold: "",
	});
	const [isSavingConfig, setIsSavingConfig] = useState(false);

	const handleOpenConfigModal = () => {
		setConfigForm({
			sponsorRewardAmount: num(config?.sponsorRewardAmount),
			qualifyingOrderMin: num(config?.qualifyingOrderMin),
			refereeExpiryDays: num(config?.refereeExpiryDays),
			rewardDelayDays: num(config?.rewardDelayDays),
			fraudBlockThreshold: num(config?.fraudBlockThreshold),
		});
		setShowConfigModal(true);
	};

	const handleSaveConfig = async () => {
		setIsSavingConfig(true);
		try {
			await customersApi.updateReferralConfig({
				sponsorRewardAmount: configForm.sponsorRewardAmount ? Number(configForm.sponsorRewardAmount) : undefined,
				qualifyingOrderMin: configForm.qualifyingOrderMin ? Number(configForm.qualifyingOrderMin) : undefined,
				refereeExpiryDays: configForm.refereeExpiryDays ? Number(configForm.refereeExpiryDays) : undefined,
				rewardDelayDays: configForm.rewardDelayDays ? Number(configForm.rewardDelayDays) : undefined,
				fraudBlockThreshold: configForm.fraudBlockThreshold ? Number(configForm.fraudBlockThreshold) : undefined,
			});
			toast.success("Règles du programme de parrainage enregistrées.");
			setShowConfigModal(false);
			refetchConfig();
		} catch (err: unknown) {
			const ax = err as { response?: { data?: { message?: string } } };
			toast.error(ax.response?.data?.message ?? "Erreur lors de la mise à jour des règles.");
		} finally {
			setIsSavingConfig(false);
		}
	};

	// Analytics calculés à partir des données réelles de parrainage
	const filteredReferrals = useMemo(() => {
		return (referrals ?? []).filter((r) => {
			if (!searchQuery.trim()) return true;
			const q = searchQuery.toLowerCase().trim();
			return (
				r.id.toLowerCase().includes(q) ||
				r.sponsorId.toLowerCase().includes(q) ||
				(r.refereeId && r.refereeId.toLowerCase().includes(q))
			);
		});
	}, [referrals, searchQuery]);

	const stats = useMemo(() => {
		const all = referrals ?? [];
		const total = all.length;
		const rewarded = all.filter((r) => r.status === "rewarded").length;
		const qualified = all.filter((r) => r.status === "qualified").length;
		const flagged = all.filter((r) => r.status === "flagged" || r.fraudScore >= ((config?.fraudBlockThreshold as number) ?? 70)).length;
		const totalRewardsPaid = all
			.filter((r) => r.status === "rewarded")
			.reduce((acc, r) => acc + (r.sponsorRewardAmount || 0), 0);

		return { total, rewarded, qualified, flagged, totalRewardsPaid };
	}, [referrals, config]);

	return (
		<>
			<PageHead
				title="Programme Parrainage"
				sub="Suivi des conversions, classements des ambassadeurs et sécurité anti-abus"
				actions={
					canEditConfig && (
						<AdminButton variant="primary" onClick={handleOpenConfigModal}>
							<AdminIcon n="gear" s={14} /> Modifier la configuration
						</AdminButton>
					)
				}
			/>

			{/* Bandeau synthétique des règles du programme */}
			<div
				style={{
					background: "var(--a-surface, #fff)",
					border: "1px solid var(--a-border)",
					borderRadius: 10,
					padding: "10px 16px",
					marginBottom: 16,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 12,
					flexWrap: "wrap",
				}}
			>
				<div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", fontSize: 13 }}>
					<span style={{ fontWeight: 700, color: "var(--a-ink-main)", display: "flex", alignItems: "center", gap: 6 }}>
						<AdminIcon n="zap" s={14} c="var(--a-accent, #2563eb)" /> Règles Actives :
					</span>
					<span style={{ color: "var(--a-ink-soft)" }}>
						Récompense Parrain : <strong style={{ color: "var(--a-ink-main)" }}>{isCfgLoading ? "—" : formatFcfa(config?.sponsorRewardAmount)}</strong>
					</span>
					<span style={{ color: "var(--a-border)" }}>|</span>
					<span style={{ color: "var(--a-ink-soft)" }}>
						1re Commande Min : <strong style={{ color: "var(--a-ink-main)" }}>{isCfgLoading ? "—" : formatFcfa(config?.qualifyingOrderMin)}</strong>
					</span>
					<span style={{ color: "var(--a-border)" }}>|</span>
					<span style={{ color: "var(--a-ink-soft)" }}>
						Expiration : <strong style={{ color: "var(--a-ink-main)" }}>{isCfgLoading ? "—" : `${num(config?.refereeExpiryDays)} j`}</strong>
					</span>
					<span style={{ color: "var(--a-border)" }}>|</span>
					<span style={{ color: "var(--a-ink-soft)" }}>
						Seuil Anti-Fraude : <strong style={{ color: "var(--a-ink-main)" }}>Score ≥ {num(config?.fraudBlockThreshold)}</strong>
					</span>
				</div>
				<Tag variant="success" noDot>
					Programme Actif
				</Tag>
			</div>

			{/* Cartes KPI Essentielles (4 Métriques Haute Valeur) */}
			<div className="kpi-grid" style={{ marginBottom: 20 }}>
				<Kpi
					label="Total Parrainages"
					value={stats.total}
					icon="users"
					variant="default"
				/>
				<Kpi
					label="Parrainages Récompensés"
					value={stats.rewarded}
					icon="check"
					variant="forest"
					foot={`${stats.total ? Math.round((stats.rewarded / stats.total) * 100) : 0}% de conversion`}
				/>
				<Kpi
					label="Total Distribué"
					value={formatFcfa(stats.totalRewardsPaid)}
					icon="zap"
					variant="accent"
				/>
				<Kpi
					label="Alertes Anti-Fraude"
					value={stats.flagged}
					icon="alert"
					variant={stats.flagged > 0 ? "warning" : "default"}
				/>
			</div>

			<div className="grid-2" style={{ alignItems: "start" }}>
				{/* Tableau des parrainages */}
				<Card style={{ alignSelf: "start" }}>
					<CardHead
						title="Parrainages Récents"
						sub={`${filteredReferrals.length} parrainage(s)`}
						right={
							<div style={{ display: "flex", gap: 10, alignItems: "center" }}>
								<div style={{ position: "relative", width: 220 }}>
									<input
										className="inp"
										style={{ width: "100%", paddingLeft: 30, fontSize: 13, height: 34 }}
										placeholder="Rechercher parrain / filleul..."
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
									/>
									<div style={{ position: "absolute", left: 9, top: 9 }}>
										<AdminIcon n="search" s={14} c="var(--a-ink-mute)" />
									</div>
								</div>
								<select
									className="inp"
									style={{ fontSize: 13, height: 34, width: 160 }}
									value={statusFilter}
									onChange={(e) => setStatusFilter(e.target.value)}
									aria-label="Filtre statut parrainage"
								>
									<option value="">Tous les statuts</option>
									<option value="pending">En attente</option>
									<option value="qualified">Qualifiés</option>
									<option value="rewarded">Récompensés</option>
									<option value="flagged">Suspects / Fraude</option>
									<option value="expired">Expirés</option>
								</select>
							</div>
						}
					/>

					<div className="table-wrap" style={{ margin: 0 }}>
						<table className="table">
							<thead>
								<tr>
									<th>Statut</th>
									<th>Parrain</th>
									<th>Filleul</th>
									<th className="right">Récompense</th>
									<th className="right">Score Fraude</th>
									<th className="right">Créé le</th>
								</tr>
							</thead>
							<tbody>
								{isRefLoading && (
									<tr>
										<td colSpan={6} className="muted" style={{ textAlign: "center", padding: 30 }}>
											<div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
												<AdminIcon n="clock" s={16} /> Chargement des parrainages…
											</div>
										</td>
									</tr>
								)}
								{isRefError && (
									<tr>
										<td colSpan={6} className="muted" style={{ textAlign: "center", padding: 30, color: "var(--a-danger)" }}>
											Erreur lors du chargement des parrainages.
										</td>
									</tr>
								)}
								{!isRefLoading && !isRefError && filteredReferrals.length === 0 && (
									<tr>
										<td colSpan={6} style={{ textAlign: "center", padding: 40 }}>
											<div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
												<div style={{ background: "var(--a-bg-soft, #f4f4f5)", padding: 12, borderRadius: "50%" }}>
													<AdminIcon n="users" s={24} c="var(--a-ink-mute)" />
												</div>
												<div style={{ fontWeight: 600, color: "var(--a-ink-main)" }}>Aucun parrainage trouvé</div>
												<div style={{ fontSize: 12, color: "var(--a-ink-mute)" }}>
													Modifiez vos critères de recherche pour afficher des parrainages.
												</div>
											</div>
										</td>
									</tr>
								)}
								{filteredReferrals.map((r: Referral) => (
									<tr key={r.id}>
										<td>
											<Tag variant={STATUS_VARIANT[r.status] ?? "neutral"}>
												{STATUS_LABEL[r.status] ?? r.status}
											</Tag>
										</td>
										<td>
											<span className="mono" style={{ fontSize: 12, fontWeight: 600, color: "var(--a-ink-main)" }}>
												{r.sponsorId.slice(0, 12)}…
											</span>
										</td>
										<td>
											<span className="mono" style={{ fontSize: 12, color: "var(--a-ink-soft)" }}>
												{r.refereeId ? `${r.refereeId.slice(0, 12)}…` : "—"}
											</span>
										</td>
										<td className="right tn" style={{ fontWeight: 700, color: "var(--a-ink-main)" }}>
											{formatFcfa(r.sponsorRewardAmount)}
										</td>
										<td className="right tn">
											<Tag
												variant={
													r.fraudScore >= 70
														? "terra"
														: r.fraudScore >= 40
															? "warning"
															: "success"
												}
												noDot
											>
												{r.fraudScore} / 100
											</Tag>
										</td>
										<td className="right muted" style={{ fontSize: 12 }}>
											{new Date(r.createdAt).toLocaleDateString("fr-FR", {
												day: "2-digit",
												month: "short",
												year: "numeric",
											})}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</Card>

				{/* Leaderboard Parrains */}
				<Card style={{ alignSelf: "start" }}>
					<CardHead
						title="Leaderboard Parrains"
						sub="Classement des meilleurs ambassadeurs"
						right={
							<Tag variant="info" noDot>
								<AdminIcon n="star" s={12} /> Top Ambassadeurs
							</Tag>
						}
					/>
					<CardBody>
						{isLbLoading && (
							<div className="muted" style={{ textAlign: "center", padding: 20 }}>
								Chargement du classement…
							</div>
						)}
						{!isLbLoading && (leaderboard ?? []).length === 0 && (
							<div style={{ textAlign: "center", padding: "30px 10px" }}>
								<div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
									<AdminIcon n="star" s={24} c="var(--a-ink-mute)" />
									<div style={{ fontSize: 13, fontWeight: 600, color: "var(--a-ink-soft)" }}>
										Aucune donnée de parrainage
									</div>
									<div style={{ fontSize: 12, color: "var(--a-ink-mute)" }}>
										Les ambassadeurs apparaîtront ici dès leurs premiers parrainages.
									</div>
								</div>
							</div>
						)}
						{(leaderboard ?? []).map((l, i) => {
							const rankColor =
								i === 0
									? "#d97706"
									: i === 1
										? "#475569"
										: i === 2
											? "#b45309"
											: "var(--a-ink-mute)";
							const rankBg =
								i === 0
									? "var(--a-ochre-soft, #fef3c7)"
									: i === 1
										? "var(--a-bg-soft, #f1f5f9)"
										: i === 2
											? "var(--a-ochre-soft, #fff7ed)"
											: "transparent";
							const pct = l.total > 0 ? Math.round((l.rewarded / l.total) * 100) : 0;

							return (
								<div
									key={l.sponsorId}
									style={{
										display: "flex",
										flexDirection: "column",
										gap: 6,
										padding: "12px 14px",
										marginBottom: 8,
										borderRadius: 10,
										border: "1px solid var(--a-border)",
										background: "var(--a-surface, #fff)",
									}}
								>
									<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
										<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
											<span
												style={{
													width: 26,
													height: 26,
													borderRadius: "50%",
													background: rankBg,
													color: rankColor,
													fontWeight: 800,
													fontSize: 12,
													display: "grid",
													placeItems: "center",
													border: `1px solid ${i <= 2 ? rankColor : "var(--a-border)"}`,
												}}
											>
												#{i + 1}
											</span>
											<div>
												<div style={{ fontSize: 13, fontWeight: 700, color: "var(--a-ink-main)" }}>
													Parrain
												</div>
												<div className="mono muted" style={{ fontSize: 11 }}>
													{l.sponsorId}
												</div>
											</div>
										</div>

										<div style={{ textAlign: "right" }}>
											<div className="tn" style={{ fontWeight: 700, fontSize: 13, color: "var(--a-ink-main)" }}>
												{l.rewarded} / {l.total} qualifiés
											</div>
											<div style={{ fontSize: 11, color: "var(--a-ink-mute)" }}>
												{pct}% de conversion
											</div>
										</div>
									</div>

									{/* Barre de progression mini */}
									<div
										style={{
											width: "100%",
											height: 4,
											background: "var(--a-bg-soft, #e2e8f0)",
											borderRadius: 2,
											overflow: "hidden",
											marginTop: 2,
										}}
									>
										<div
											style={{
												width: `${pct}%`,
												height: "100%",
												background: i === 0 ? "var(--a-accent, #2563eb)" : "var(--a-forest, #059669)",
												borderRadius: 2,
												transition: "width 0.3s ease",
											}}
										/>
									</div>
								</div>
							);
						})}
					</CardBody>
				</Card>
			</div>

			{/* Modale de modification de la configuration */}
			{showConfigModal && (
				<div role="dialog" aria-modal="true" style={modalOverlayStyle}>
					<div style={modalBoxStyle}>
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
							<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
								<div style={{ background: "var(--a-accent-soft, #eff6ff)", padding: 8, borderRadius: 8 }}>
									<AdminIcon n="gear" s={18} c="var(--a-accent, #2563eb)" />
								</div>
								<div>
									<h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Configuration Parrainage</h3>
									<p style={{ margin: 0, fontSize: 12, color: "var(--a-ink-soft)" }}>
										Ajustez les récompenses et seuils de qualification
									</p>
								</div>
							</div>
							<AdminButton variant="ghost" size="sm" onClick={() => setShowConfigModal(false)}>
								<AdminIcon n="close" s={16} />
							</AdminButton>
						</div>

						<div style={{ display: "grid", gap: 14, marginTop: 4 }}>
							<label style={{ display: "grid", gap: 4 }}>
								<span style={{ fontSize: 12, fontWeight: 600, color: "var(--a-ink-main)" }}>
									Montant Récompense Parrain (FCFA)
								</span>
								<input
									className="inp"
									placeholder="Ex: 1000"
									value={configForm.sponsorRewardAmount}
									onChange={(e) => setConfigForm({ ...configForm, sponsorRewardAmount: e.target.value })}
								/>
								<span style={{ fontSize: 11, color: "var(--a-ink-mute)" }}>
									Crédit versé sur le portefeuille du parrain lors de la qualification du filleul.
								</span>
							</label>

							<label style={{ display: "grid", gap: 4 }}>
								<span style={{ fontSize: 12, fontWeight: 600, color: "var(--a-ink-main)" }}>
									Montant 1re Commande Minimum Filleul (FCFA)
								</span>
								<input
									className="inp"
									placeholder="Ex: 15000"
									value={configForm.qualifyingOrderMin}
									onChange={(e) => setConfigForm({ ...configForm, qualifyingOrderMin: e.target.value })}
								/>
								<span style={{ fontSize: 11, color: "var(--a-ink-mute)" }}>
									Seuil d'achat minimum requis pour déclencher la récompense.
								</span>
							</label>

							<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
								<label style={{ display: "grid", gap: 4 }}>
									<span style={{ fontSize: 12, fontWeight: 600, color: "var(--a-ink-main)" }}>
										Expiration Filleul (jours)
									</span>
									<input
										className="inp"
										placeholder="Ex: 90"
										value={configForm.refereeExpiryDays}
										onChange={(e) => setConfigForm({ ...configForm, refereeExpiryDays: e.target.value })}
									/>
								</label>

								<label style={{ display: "grid", gap: 4 }}>
									<span style={{ fontSize: 12, fontWeight: 600, color: "var(--a-ink-main)" }}>
										Délai Récompense (jours)
									</span>
									<input
										className="inp"
										placeholder="Ex: 7"
										value={configForm.rewardDelayDays}
										onChange={(e) => setConfigForm({ ...configForm, rewardDelayDays: e.target.value })}
									/>
								</label>
							</div>

							<label style={{ display: "grid", gap: 4 }}>
								<span style={{ fontSize: 12, fontWeight: 600, color: "var(--a-ink-main)" }}>
									Seuil de Blocage Anti-Fraude (Score 0-100)
								</span>
								<input
									className="inp"
									placeholder="Ex: 70"
									value={configForm.fraudBlockThreshold}
									onChange={(e) => setConfigForm({ ...configForm, fraudBlockThreshold: e.target.value })}
								/>
								<span style={{ fontSize: 11, color: "var(--a-ink-mute)" }}>
									Les parrainages ayant un score supérieur à ce seuil seront automatiquement bloqués.
								</span>
							</label>
						</div>

						<div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--a-border)" }}>
							<AdminButton variant="ghost" onClick={() => setShowConfigModal(false)}>
								Annuler
							</AdminButton>
							<AdminButton variant="primary" disabled={isSavingConfig} onClick={handleSaveConfig}>
								{isSavingConfig ? "Enregistrement…" : "Enregistrer les modifications"}
							</AdminButton>
						</div>
					</div>
				</div>
			)}
		</>
	);
}

const modalOverlayStyle = {
	position: "fixed" as const,
	inset: 0,
	zIndex: 1000,
	background: "rgba(15, 23, 42, 0.5)",
	backdropFilter: "blur(4px)",
	display: "grid",
	placeItems: "center",
	padding: 16,
};

const modalBoxStyle = {
	width: "min(500px, 100%)",
	background: "var(--a-surface, #fff)",
	border: "1px solid var(--a-border)",
	borderRadius: 14,
	boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
	padding: 24,
	display: "grid",
	gap: 16,
};

